"use client";

import { Coupon } from "@/src/types/tenant";
import React, { useState } from "react";

interface Offer {
  id: string;
  icon: "percent" | "gift" | "card" | "bundle";
  title: string;
  desc?: string;
  code: string;
  expiry?: string;
  theme: "sun" | "green" | "indigo" | "pink";
}

interface ActiveOffersProps {
  coupons?: Coupon[];
  tenantName?: string;
}

const THEMES = ["sun", "green", "indigo", "pink"] as const;
const ICONS = ["percent", "gift", "card", "bundle"] as const;

const THEME_STYLES: Record<string, { bg: string; border: string; iconColor: string }> = {
  sun:    { bg: "linear-gradient(135deg, #FFE9DF 0%, #FFF4DA 100%)", border: "rgba(228,96,78,0.3)", iconColor: "var(--rising-sun)" },
  green:  { bg: "linear-gradient(135deg, #E8F0E5 0%, #F2F7F0 100%)", border: "rgba(53,73,48,0.25)", iconColor: "var(--chutney-green)" },
  indigo: { bg: "linear-gradient(135deg, #E0EAF7 0%, #EEF3FB 100%)", border: "rgba(8,61,118,0.25)", iconColor: "var(--indigo)" },
  pink:   { bg: "linear-gradient(135deg, #FDE6E0 0%, #FFF1ED 100%)", border: "rgba(244,172,153,0.5)", iconColor: "var(--rising-sun)" },
};

function OfferIcon({ type, color }: { type: string; color: string }) {
  const style = { color };
  if (type === "percent") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={style}>
      <line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
  if (type === "gift") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
    </svg>
  );
  if (type === "card") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

export const ActiveOffers: React.FC<ActiveOffersProps> = ({
  coupons = [],
  tenantName = "this shop",
}) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const offers: Offer[] = coupons.filter((c) => c.code).map((c, i) => ({
    id: String(c.id),
    icon: ICONS[i % ICONS.length],
    title: c.name,
    desc: c.description,
    code: c.code ?? "",
    expiry: undefined,
    theme: THEMES[i % THEMES.length],
  }));

  if (offers.length === 0) return null;

  const handleCopy = async (code: string) => {
    try {
      if (navigator.clipboard) {
        // HTTPS / localhost — modern API
        await navigator.clipboard.writeText(code);
      } else {
        // HTTP fallback (lvh.me dev env) — legacy execCommand
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch {
      // If all else fails, still show the feedback
    }
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Section head */}
      <div className="mb-[clamp(16px,2vw,24px)] flex items-end justify-between gap-4">
        <div>
          <h2
            className="font-bold leading-tight"
            style={{ fontSize: "clamp(18px,2.5vw,24px)", letterSpacing: "-0.5px" }}
          >
            Active offers from {tenantName}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
            Tap to copy. Conditions apply.
          </p>
        </div>
        {offers.length > 4 && (
          <button
            onClick={() => setShowAll((p) => !p)}
            className="flex shrink-0 items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70"
            style={{ color: "var(--techno-black-70)" }}
          >
            {showAll ? "Show less" : `See all ${offers.length}`}
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
              className="transition-transform"
              style={{ transform: showAll ? "rotate(90deg)" : "none" }}
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Offer cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(showAll ? offers : offers.slice(0, 4)).map((offer) => {
          const { bg, border, iconColor } = THEME_STYLES[offer.theme];
          const isCopied = copied === offer.code;
          return (
            <div
              key={offer.id}
              className="relative flex flex-col gap-2.5 rounded-[14px] border p-[18px] transition-transform hover:-translate-y-0.5"
              style={{ background: bg, borderColor: border, borderStyle: "dashed" }}
            >
              {/* Ticket notches */}
              <div
                className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ width: 14, height: 14, background: "var(--rev-white)" }}
              />
              <div
                className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ width: 14, height: 14, background: "var(--rev-white)" }}
              />

              {/* Header */}
              <div className="flex items-center gap-2 text-[10.5px] font-bold tracking-[1.6px] uppercase" style={{ color: "var(--techno-black-70)" }}>
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-[6px]"
                  style={{ background: "rgba(255,255,255,0.7)" }}
                >
                  <OfferIcon type={offer.icon} color={iconColor} />
                </span>
                OFFER
              </div>

              {/* Title + desc */}
              <div
                className="font-bold leading-snug"
                style={{ fontSize: 17, letterSpacing: "-0.3px", color: "var(--techno-black)" }}
              >
                {offer.title}
              </div>
              {offer.desc && (
                <div className="flex-1 text-[13px] leading-snug" style={{ color: "var(--techno-black-70)" }}>
                  {offer.desc}
                </div>
              )}

              {/* Footer */}
              <div
                className="mt-1 flex items-center justify-between gap-2 pt-2.5"
                style={{ borderTop: "1px dashed rgba(34,34,40,0.15)" }}
              >
                <code
                  className="flex items-center gap-2 rounded-[6px] border px-2.5 py-1.5 font-bold tracking-[0.5px]"
                  style={{
                    fontFamily: "var(--font-code)",
                    fontSize: "12.5px",
                    color: "var(--techno-black)",
                    background: "rgba(255,255,255,0.7)",
                    borderColor: "rgba(34,34,40,0.25)",
                    borderStyle: "dashed",
                  }}
                >
                  {offer.code}
                </code>
                <button
                  onClick={() => handleCopy(offer.code)}
                  className="rounded-[6px] px-2.5 py-1.5 text-[12px] font-semibold transition"
                  style={{
                    color: isCopied ? "var(--chutney-green)" : "var(--techno-black-70)",
                  }}
                >
                  {isCopied ? "Copied ✓" : "Copy"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
